import * as Sentry from '@sentry/react-native';

import { scrubEvent, type ScrubbableEvent } from '@rbf/shared';

// Sentry error/crash reporting for the native (iOS/Android) and web surfaces.
//
// The unified @sentry/react-native SDK detects `Platform.OS === 'web'` and
// delegates to the browser SDK there, so this single init covers all three
// targets. Import this module once (in src/app/_layout.tsx) before render.
//
// Init is GATED on EXPO_PUBLIC_SENTRY_DSN: unset = fully disabled. This keeps
// local dev, Expo Go, CI, and tests clean with zero config, and lets the wiring
// ship before a DSN exists. `Sentry.wrap` is inert when init was skipped.
const dsn = process.env.EXPO_PUBLIC_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    // Defaults to 'dev' under Metro (`__DEV__`) and 'production' in release /
    // `expo export` builds; EXPO_PUBLIC_SENTRY_ENVIRONMENT overrides if set.
    environment:
      process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT ?? (__DEV__ ? 'dev' : 'production'),
    // No `tracesSampleRate`: tracing is off deliberately. `beforeSend` fires for ERROR
    // events only, so transaction events would bypass the scrubber below. Re-enabling
    // needs BOTH a `beforeSendTransaction` hook AND `scrubEvent` extended to redact
    // `spans[].description` / `spans[].data` — an http.client span description is a raw URL.
    //
    // Strip PII before any event leaves the device: drop user email/ip, auth
    // headers, cookies, and request bodies, and redact emails / tokens from the
    // free-text an error can smuggle them through (exception messages, event
    // message, breadcrumbs, extra, contexts). Breadcrumbs are attached to the
    // event before beforeSend, so scrubEvent covers them — no separate
    // beforeBreadcrumb needed. See @rbf/shared scrub.ts.
    beforeSend: (event) =>
      scrubEvent(event as unknown as ScrubbableEvent) as unknown as typeof event,
  });
}

export { Sentry };
